import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { permissionsFactory } from "../utils/utils";
import { apiInstance } from "../services/api_service";
import Page403 from "./403";
import { toast } from 'react-toastify';

const StudentsEnrollmentInfo = () => {
  const { permissions } = useAuth();
  const grantedPermissions = permissionsFactory(permissions);
  
  const [segments, setSegments] = useState([]);
  const [courses, setCourses] = useState({});
  const [classes, setClasses] = useState({});
  const [expandedSegments, setExpandedSegments] = useState(new Set());
  const [expandedCourses, setExpandedCourses] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state for student list
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);
  
  // Selection state for multiple students
  const [selectedStudents, setSelectedStudents] = useState(new Set());

  // Define the order for segments
  const segmentOrder = ['preschool', 'elementary-I', 'elementary-II', 'high-school'];
  
  // Define the order for preschool courses
  const preschoolCourseOrder = ['TODDLER', 'NURSERY', 'JUNIOR_KINDERGARTEN', 'SENIOR_KINDERGARTEN'];

  useEffect(() => {
    if (grantedPermissions.canViewStudent) {
      fetchSegments();
    }
  }, [grantedPermissions.canViewStudent]);

  if (!grantedPermissions.canViewStudent) {
    return <Page403 />;
  }

  const fetchSegments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiInstance.get('students/all-enrolled/segment-counts/');
      setSegments(response.data);
    } catch (err) {
      console.error('Error fetching segments:', err);
      setError('Erro ao carregar segmentos');
      toast.error('Erro ao carregar segmentos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async (segmentName) => {
    try {
      const response = await apiInstance.get(`students/all-enrolled/course-counts/?enrollment__student_class__segment__in=${segmentName}`);
      setCourses(prev => ({
        ...prev,
        [segmentName]: response.data
      }));
    } catch (err) {
      console.error('Error fetching courses:', err);
      toast.error('Erro ao carregar cursos');
    }
  };

  const fetchClasses = async (courseName) => {
    try {
      const response = await apiInstance.get(`students/all-enrolled/class-counts/?enrollment__student_class__course=${courseName}`);
      setClasses(prev => ({
        ...prev,
        [courseName]: response.data
      }));
    } catch (err) {
      console.error('Error fetching classes:', err);
      toast.error('Erro ao carregar turmas');
    }
  };

  const fetchStudentsByClass = async (classId) => {
    try {
      setStudentsLoading(true);
      setStudentsError(null);
      const response = await apiInstance.get(`students/all-enrolled/?enrollment__student_class__pk=${classId}&page_size=150`);
      setStudents(response.data.results);
    } catch (err) {
      console.error('Error fetching students:', err);
      setStudentsError('Erro ao carregar alunos');
      toast.error('Erro ao carregar alunos');
    } finally {
      setStudentsLoading(false);
    }
  };

  const toggleSegment = (segmentName) => {
    const newExpanded = new Set(expandedSegments);
    if (newExpanded.has(segmentName)) {
      newExpanded.delete(segmentName);
    } else {
      newExpanded.add(segmentName);
      // Fetch courses if not already loaded
      if (!courses[segmentName]) {
        fetchCourses(segmentName);
      }
    }
    setExpandedSegments(newExpanded);
  };

  const toggleCourse = (courseName) => {
    const newExpanded = new Set(expandedCourses);
    if (newExpanded.has(courseName)) {
      newExpanded.delete(courseName);
    } else {
      newExpanded.add(courseName);
      // Fetch classes if not already loaded
      if (!classes[courseName]) {
        fetchClasses(courseName);
      }
    }
    setExpandedCourses(newExpanded);
  };

  const handleClassClick = (classItem) => {
    setSelectedClass(classItem);
    setIsModalOpen(true);
    fetchStudentsByClass(classItem.id);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedClass(null);
    setStudents([]);
    setStudentsError(null);
    setSelectedStudents(new Set());
  };

  const copyStudentName = async (studentName) => {
    try {
      await navigator.clipboard.writeText(studentName);
      toast.success(`Nome copiado: ${studentName}`, {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error('Erro ao copiar nome', {
        position: "top-right",
        autoClose: 2000,
      });
    }
  };

  const copyAllStudentNames = async () => {
    try {
      const allNames = students.map(student => student.name).join('\n');
      await navigator.clipboard.writeText(allNames);
      toast.success(`${students.length} nomes copiados para a área de transferência`, {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error('Erro ao copiar lista de nomes', {
        position: "top-right",
        autoClose: 2000,
      });
    }
  };

  const toggleStudentSelection = (studentId) => {
    const newSelection = new Set(selectedStudents);
    if (newSelection.has(studentId)) {
      newSelection.delete(studentId);
    } else {
      newSelection.add(studentId);
    }
    setSelectedStudents(newSelection);
  };

  const selectAllStudents = () => {
    const allStudentIds = new Set(students.map(student => student.id));
    setSelectedStudents(allStudentIds);
  };

  const deselectAllStudents = () => {
    setSelectedStudents(new Set());
  };

  const copySelectedStudentNames = async () => {
    try {
      const selectedNames = students
        .filter(student => selectedStudents.has(student.id))
        .map(student => student.name)
        .join('\n');
      
      await navigator.clipboard.writeText(selectedNames);
      toast.success(`${selectedStudents.size} nomes selecionados copiados para a área de transferência`, {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error('Erro ao copiar nomes selecionados', {
        position: "top-right",
        autoClose: 2000,
      });
    }
  };

  const getSegmentDisplayName = (segmentName) => {
    const names = {
      'preschool': 'Infantil',
      'elementary-I': 'Fundamental I',
      'elementary-II': 'Fundamental II',
      'high-school': 'Médio'
    };
    return names[segmentName] || segmentName;
  };

  const getCourseDisplayName = (courseName) => {
    const preschoolNames = {
      'JUNIOR_KINDERGARTEN': 'Junior Kindergarten',
      'NURSERY': 'Nursery',
      'SENIOR_KINDERGARTEN': 'Senior Kindergarten',
      'TODDLER': 'Toddler'
    };
    
    // Return preschool names if it's a preschool course
    if (preschoolNames[courseName]) {
      return preschoolNames[courseName];
    }
    
    // For other courses, convert YEAR_X format to "Year X"
    if (courseName.startsWith('YEAR_')) {
      const yearNumber = courseName.replace('YEAR_', '');
      return `Year ${yearNumber}`;
    }
    
    // Return as-is if it doesn't match any pattern
    return courseName;
  };

  // Sort segments according to the defined order
  const sortedSegments = segments.sort((a, b) => {
    const indexA = segmentOrder.indexOf(a.name);
    const indexB = segmentOrder.indexOf(b.name);
    return indexA - indexB;
  });

  // Sort courses for preschool segment
  const sortCourses = (courses, segmentName) => {
    if (segmentName === 'preschool') {
      return courses.sort((a, b) => {
        const indexA = preschoolCourseOrder.indexOf(a.name);
        const indexB = preschoolCourseOrder.indexOf(b.name);
        return indexA - indexB;
      });
    }
    return courses; // Return unsorted for other segments
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-mp-dark mb-2">
            Informações gerais de matrícula
          </h1>
          <p className="text-gray-600">
            Os totais representam alunos rematriculados e novos.
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mp-red mx-auto mb-4"></div>
            <p className="text-gray-500">Carregando dados...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-mp-dark mb-2">
            Informações gerais de matrícula
          </h1>
          <p className="text-gray-600">
            Os totais representam alunos rematriculados e novos.
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center py-12">
            <svg 
              width="64" 
              height="64" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="mx-auto mb-4 text-red-400"
            >
              <path 
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            <h3 className="text-lg font-semibold text-red-600 mb-2">
              Erro ao carregar dados
            </h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <button 
              onClick={fetchSegments}
              className="px-4 py-2 bg-mp-red text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-mp-dark mb-2">
          Informações gerais de matrícula
        </h1>
        <p className="text-gray-600">
          Os totais representam alunos rematriculados e novos.
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg shadow-lg p-6">
        {sortedSegments.map((segment) => (
          <div key={segment.name} className="mb-4">
            {/* Segment Header */}
            <div 
              className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg p-4 cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 shadow-sm"
              onClick={() => toggleSegment(segment.name)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div>
                    <h3 className="text-lg font-bold text-blue-900">
                      {getSegmentDisplayName(segment.name)}
                    </h3>
                    <p className="text-sm text-blue-600">Segmento</p>
                  </div>
                  <svg 
                    className={`w-5 h-5 ml-4 text-blue-600 transition-transform ${expandedSegments.has(segment.name) ? 'rotate-90' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <div className="flex items-center">
                  <span className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md">
                    {segment.count || 0} alunos
                  </span>
                </div>
              </div>
            {/* Courses */}
            {expandedSegments.has(segment.name) && (
              <div className="mt-3 ml-6 space-y-2">
                {courses[segment.name] ? (
                  sortCourses(courses[segment.name], segment.name).map((course) => (
                    <div key={course.name} className={`bg-gradient-to-r from-purple-50 to-violet-50 border-l-4 border-purple-400 rounded-lg ${expandedCourses.has(course.name) ? 'pb-3' : ''}`}>
                      {/* Course Header */}
                      <div 
                        className="p-3 cursor-pointer hover:from-purple-100 hover:to-violet-100 transition-all duration-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCourse(course.name);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div>
                              <h4 className="text-md font-semibold text-purple-800">
                                {getCourseDisplayName(course.name)}
                              </h4>
                              <p className="text-xs text-purple-600">Curso</p>
                            </div>
                            <svg 
                              className={`w-4 h-4 ml-3 text-purple-600 transition-transform ${expandedCourses.has(course.name) ? 'rotate-90' : ''}`}
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                          <span className="bg-purple-600 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-sm">
                            {course.count} alunos
                          </span>
                        </div>
                      </div>

                      {/* Classes */}
                      {expandedCourses.has(course.name) && (
                        <div className="mt-2 ml-6 space-y-1">
                          {classes[course.name] ? (
                            classes[course.name].map((classItem) => (
                              <div key={classItem.id} className="bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-400 rounded-lg p-3 hover:from-red-100 hover:to-rose-100 transition-all duration-200" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <h5 className="text-sm font-semibold text-red-800">
                                          {classItem.name}
                                        </h5>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleClassClick(classItem);
                                          }}
                                          className="text-gray-600 hover:text-gray-800 text-xs font-medium underline transition-colors cursor-pointer"
                                        >
                                          Ver alunos
                                        </button>
                                      </div>
                                      <p className="text-xs text-red-600">Turma</p>
                                    </div>
                                  </div>
                                  <span className="bg-red-600 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-sm">
                                    {classItem.count} alunos
                                  </span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-3 ml-6">
                              <div className="animate-pulse flex items-center">
                                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                <div className="h-6 bg-gray-200 rounded-full w-16 ml-auto"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-4 pl-12">
                    <div className="animate-pulse flex items-center">
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-6 bg-gray-200 rounded-full w-16 ml-auto"></div>
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Student List Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[80vw] h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-mp-dark">
                  {selectedClass?.name}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Selection Controls */}
              {students.length > 0 && (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAllStudents}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      Selecionar Todos
                    </button>
                    <button
                      onClick={deselectAllStudents}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Desmarcar Todos
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    {selectedStudents.size} de {students.length} selecionados
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {studentsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mp-red mx-auto mb-4"></div>
                  <p className="text-gray-500">Carregando alunos...</p>
                </div>
              ) : studentsError ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-red-600 mb-2">Erro ao carregar alunos</h3>
                  <p className="text-gray-500 mb-4">{studentsError}</p>
                  <button 
                    onClick={() => selectedClass && fetchStudentsByClass(selectedClass.id)}
                    className="px-4 py-2 bg-mp-red text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Tentar novamente
                  </button>
                </div>
              ) : students.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">Nenhum aluno encontrado</h3>
                  <p className="text-gray-500">Esta turma não possui alunos matriculados.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {students.map((student) => (
                    <div key={student.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedStudents.has(student.id)}
                            onChange={() => toggleStudentSelection(student.id)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          <h4 
                            className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                            onClick={() => copyStudentName(student.name)}
                          >
                            {student.name}
                          </h4>
                        </div>
                        <button
                          onClick={() => copyStudentName(student.name)}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copiar nome"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  Total: {students.length} alunos
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={copySelectedStudentNames}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    disabled={selectedStudents.size === 0}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copiar Selecionados ({selectedStudents.size})
                  </button>
                  <button
                    onClick={copyAllStudentNames}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    disabled={students.length === 0}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copiar Todos
                  </button>
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsEnrollmentInfo;
